/**
 * Notification Service
 * @description Service for managing notifications
 */
import type { NotificationsResponse, NotificationStats, Notification } from '@/types/notification';
import { apiRoutes } from '@/config/routes';
import api from './api';

/**
 * Get all notifications
 * @returns Promise with notifications response
 */
export const getNotifications = async (): Promise<NotificationsResponse> => {
  return await api.get<NotificationsResponse>(apiRoutes.notifications.list);
};

/**
 * Get notification statistics
 * @returns Promise with notification stats
 */
export const getNotificationStats = async (): Promise<NotificationStats> => {
  return await api.get<NotificationStats>(apiRoutes.notifications.stats);
};

/**
 * Mark a notification as read
 * @param notificationId - The notification ID (number or string)
 * @returns Promise with updated notification
 */
export const markNotificationAsRead = async (notificationId: string | number): Promise<Notification> => {
  const response = await api.post<Notification | { notification?: Notification }>(
    apiRoutes.notifications.markAsRead(notificationId)
  );
  // Handle different response structures
  if ('notification' in response && response.notification) {
    return response.notification;
  }
  return response as Notification;
};

/**
 * Mark a notification as unread
 * @param notificationId - The notification ID (number or string)
 * @returns Promise with updated notification
 */
export const markNotificationAsUnread = async (notificationId: string | number): Promise<Notification> => {
  const response = await api.post<Notification | { notification?: Notification }>(
    apiRoutes.notifications.markAsUnread(notificationId)
  );
  // Handle different response structures
  if ('notification' in response && response.notification) {
    return response.notification;
  }
  return response as Notification;
};

/**
 * Delete a notification
 * @param notificationId - The notification ID (number or string)
 * @returns Promise
 */
export const deleteNotification = async (notificationId: string | number): Promise<void> => {
  await api.delete(apiRoutes.notifications.delete(notificationId));
};

/**
 * Mark all notifications as read
 * @returns Promise
 */
export const markAllNotificationsAsRead = async (): Promise<void> => {
  await api.post(apiRoutes.notifications.readAll);
};

/**
 * Mark all notifications as unread
 * @returns Promise
 */
export const markAllNotificationsAsUnread = async (): Promise<void> => {
  await api.post(apiRoutes.notifications.unreadAll);
};


/**
 * Delete all notifications
 * @returns Promise
 */
export const deleteAllNotifications = async (): Promise<void> => {
  await api.delete(apiRoutes.notifications.deleteAll);
};

